import React from 'react';
import { useUserPermissions } from '../hooks/useUserPermissions.jsx';
import { getAccessibleVendors } from '../config/vendorRegistry';
import { ConfiguratorHome } from './ConfiguratorHome';

/**
 * Route-level gate in front of every configurator route (/, /classic, /v2).
 *
 * A user with no accessible vendor gets the Configurator Home page and neither
 * shell ever mounts for them — both shells dereference `vendor.*` from their
 * first render and would otherwise be running on a vendor the user may not see.
 * Everyone else gets the shell via the render-prop child, seeded with the
 * vendor to START on: useConfigurationLogic captures its initial config once at
 * mount, so the first render has to be on the right vendor (a later snap in
 * useVendor would leave config.hardwareVendor and jbodSize on AIC values that
 * rule R9 cannot index for Dell or Supermicro).
 *
 * Children is a function so this file mounts neither shell itself and its test
 * can assert on what it was handed. It sits OUTSIDE LicenseGate on purpose:
 * Home shows nothing confidential, so a zero-vendor user is not asked to accept
 * an NDA for a page with one sentence on it. UX only — server-side
 * authorization is each model's @auth rules.
 */
export function ConfiguratorGate({ user, signOut, children }) {
  const { userGroups, hasAdminAccess, isCheckingAccess } = useUserPermissions(user);

  // Don't decide before Cognito groups resolve — that would flash Home at
  // legitimate users. Same holding pattern as admin/RequireGroup.
  if (isCheckingAccess) {
    return <div className="p-4 bg-black min-h-screen text-white">Checking access...</div>;
  }

  const accessible = getAccessibleVendors(userGroups);
  if (accessible.length === 0) {
    return (
      <ConfiguratorHome
        userName={user?.email || user?.attributes?.email || user?.username}
        showAdminLink={hasAdminAccess || userGroups.includes('catalogAuthor')}
        onSignOut={signOut}
      />
    );
  }

  return children({ initialVendorId: accessible[0] });
}

export default ConfiguratorGate;
